import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import './PostCommentModal.css';

const getImageUrl = (image) => {
  if (!image) return null;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  if (image.startsWith('/media/')) return `${apiUrl}${image}`;
  return `${apiUrl}/media/${image}`;
};

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = DOMPurify.sanitize(html);
  return tmp.textContent || tmp.innerText || '';
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function PostCommentModal({
  post,
  isOpen,
  onClose,
  commentText,
  onCommentTextChange,
  onSubmitComment,
  onLike,
  onShare,
  onSave,
  isLiked = false,
  isSaved = false,
  isSubmitting = false,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !post) return null;

  const caption =
    stripHtml(post.short_content) ||
    stripHtml(post.large_content)?.slice(0, 300) ||
    post.title ||
    '';

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (commentText?.trim() && !isSubmitting) onSubmitComment();
  };

  return (
    <div className="post-comment-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <button type="button" className="post-comment-close" onClick={onClose} aria-label="Close">
        <i className="bi bi-x-lg"></i>
      </button>

      <div className="post-comment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="post-comment-media">
          {post.image ? (
            <img
              src={getImageUrl(post.image)}
              alt={post.title || post.doctor_name || 'Post'}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/600x600?text=Image+Not+Available';
              }}
            />
          ) : (
            <div className="post-comment-media-placeholder">
              <i className="bi bi-heart-pulse fs-1 mb-3"></i>
              {post.title && <h5 className="fw-bold mb-2">{post.title}</h5>}
              <p className="mb-0 small opacity-75">{post.doctor_name || 'Health Post'}</p>
            </div>
          )}
        </div>

        <div className="post-comment-panel">
          <div className="post-comment-header">
            {post.doctor_image ? (
              <img
                src={getImageUrl(post.doctor_image)}
                alt={post.doctor_name}
                className="post-comment-avatar"
              />
            ) : (
              <div className="post-comment-avatar-fallback">
                <i className="bi bi-person-badge"></i>
              </div>
            )}
            <span className="post-comment-author">{post.doctor_name || 'Doctor'}</span>
          </div>

          <div className="post-comment-body">
            {caption && (
              <div className="post-comment-caption">
                <span className="post-comment-author">{post.doctor_name || 'Doctor'}</span>
                <span className="post-comment-caption-text">{caption}</span>
              </div>
            )}

            {post.comments && post.comments.length > 0 ? (
              post.comments.map((comment) => (
                <div key={comment.id} className="post-comment-item">
                  <div className="post-comment-item-avatar">
                    {comment.user_image || comment.doctor_image ? (
                      <img
                        src={getImageUrl(comment.user_image || comment.doctor_image)}
                        alt=""
                        className="post-comment-avatar"
                        style={{ margin: 0 }}
                      />
                    ) : (
                      <i className="bi bi-person-circle"></i>
                    )}
                  </div>
                  <div className="post-comment-item-body">
                    <div>
                      <span className="post-comment-author me-2">
                        {comment.user_name || comment.doctor_name || 'Anonymous'}
                      </span>
                      <span className="post-comment-caption-text">{comment.content}</span>
                    </div>
                    <div className="post-comment-item-meta">
                      <span>{formatTimeAgo(comment.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="post-comment-empty">No comments yet. Start the conversation.</p>
            )}
          </div>

          <div className="post-comment-footer">
            <div className="post-comment-actions">
              <button
                type="button"
                className={`post-comment-action-btn ${isLiked ? 'liked' : ''}`}
                onClick={onLike}
                aria-label={isLiked ? 'Unlike' : 'Like'}
              >
                <i className={`bi ${isLiked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
              </button>
              <button type="button" className="post-comment-action-btn" aria-label="Comment">
                <i className="bi bi-chat"></i>
              </button>
{/* {onShare && (
  <button type="button" className="post-comment-action-btn" onClick={onShare} aria-label="Share">
    <i className="bi bi-send"></i>
  </button>
)} */}
              <button
                type="button"
                className={`post-comment-action-btn ms-auto ${isSaved ? 'saved' : ''}`}
                onClick={onSave}
                aria-label={isSaved ? 'Unsave' : 'Save'}
              >
                <i className={`bi ${isSaved ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
              </button>
            </div>

            <div className="post-comment-stats">
              {(post.likes_count || 0) === 1 ? '1 like' : `${post.likes_count || 0} likes`}
            </div>
            <div className="post-comment-time">{formatTimeAgo(post.created_at)}</div>

            <form className="post-comment-input-row" onSubmit={handleSubmit}>
              <i className="bi bi-emoji-smile text-secondary" style={{ fontSize: '1.25rem' }}></i>
              <input
                ref={inputRef}
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => onCommentTextChange(e.target.value)}
              />
              <button
                type="submit"
                className="post-comment-post-btn"
                disabled={!commentText?.trim() || isSubmitting}
              >
                Post
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostCommentModal;
